<template>
  <div id="payment-reminder">
    <h1>{{ $t('PAYMENT_REMINDER') }}</h1>
    <ul v-if="(currentTab === TABS.LIST)">
      <li><strong><span>{{ `${$t('TOTAL')}: ` }}</span><span class="highlight">{{ total }}</span></strong></li>
      <li><strong><span>{{ `${$t('TOTAL_LOAN_BALANCE')}: ` }}</span><span class="highlight">{{ addCommas(loanBalance) }}</span></strong></li>
    </ul>

    <div v-if="isDesktop" class="filters">
      <text-input
        :placeholder="$t('SEARCH_BY_NAME_CLIENT_ID_PHONE_NUMBER')"
        v-model="searchInput"
        @keyup.enter="search"
        rounded-edge
        with-shadow
        borderless
      >
        <template v-slot:icon>
          <font-awesome-icon class="icon" :icon="faSearch" />
        </template>
      </text-input>

      <toggle-button
        v-if="(currentTab === TABS.LIST)"
        :text="$t('VALUE_ADJUSTMENT')"
        :khaki="!filters.hasValueAdjustment"
        :pink="filters.hasValueAdjustment"
        @click="toggleHasValueAdjustment"
        rounded-corner
      />

      <filter-box
        @filter="filter"
        :default-branches="filters.branches"
        :default-asset-types="filters.assetTypes"
        :default-min-approved-amount="filters.minApprovedAmount"
        :default-max-approved-amount="filters.maxApprovedAmount"
      />
      <date-filter
        :presets="datePresets"
        :from="filters.fromDate"
        :to="filters.toDate"
        @filter="setDate"
        blue
        rounded-corner
      />
    </div>
    <template v-else>
      <text-input
        :placeholder="$t('SEARCH_BY_NAME_CLIENT_ID_PHONE_NUMBER')"
        v-model="searchInput"
        @keyup.enter="search"
        with-shadow
        borderless
      >
        <template v-slot:icon>
          <font-awesome-icon class="icon" :icon="faSearch" />
        </template>
      </text-input>
      <div class="d-flex justify-content-around icon-buttons">
        <icon-button
          :size="35"
          :icon="faFunnelDollar"
          @click="toggleHasValueAdjustment"
          :khaki="!filters.hasValueAdjustment"
          :pink="filters.hasValueAdjustment"
        />
        <filter-box
          @filter="filter"
          :default-branches="filters.branches"
          :default-asset-types="filters.assetTypes"
          :default-min-approved-amount="filters.minApprovedAmount"
          :default-max-approved-amount="filters.maxApprovedAmount"
        />
        <date-filter
          ref="dateFilter"
          :presets="datePresets"
          :from="filters.fromDate"
          :to="filters.toDate"
          @filter="setDate"
          circle-icon-mobile
          blue
          rounded-corner
        >
        </date-filter>
      </div>
    </template>

    <tab-menu :tabs="tabItems" v-model="currentTab" />
    <past-due-clients v-if="(currentTab === TABS.LIST)" />
    <reports v-if="(currentTab === TABS.REPORTS)" />

    <client-details-modal />
  </div>
</template>

<script>
import { ref, watch, onMounted } from '@vue/composition-api';
import { createNamespacedHelpers } from 'vuex-composition-helpers';
import { addCommas } from '@/helpers';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faSearch, faPhoneAlt, faFunnelDollar, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { faChartBar } from '@fortawesome/free-regular-svg-icons';
import moment from 'moment';

import { PATHS, DATE_FORMAT, MIN_DATE } from '@/consts';

import TextInput from '@/components/TextInput';
import ToggleButton from '@/components/ToggleButton';
import TabMenu from '@/components/TabMenu';
import DateFilter from '@/components/DateFilter';
import IconButton from '@/components/IconButton';

import PastDueClients from './PastDueClients';
import Reports from './Reports';
import FilterBox from './FilterBox';
import ClientDetailsModal from './ClientDetailsModal';

const { useState, useActions } = createNamespacedHelpers('paymentReminder');
const { useState: useAppState } = createNamespacedHelpers('app');

const now = moment();

const datePresets = [
  {
    text: 'TODAY',
    range: [now.format(DATE_FORMAT), now.format(DATE_FORMAT)]
  },
  {
    text: '1_DAY_LATE',
    range: [now.clone().subtract(1, 'day').format(DATE_FORMAT), now.clone().subtract(1, 'day').format(DATE_FORMAT)]
  },
  {
    text: '2_DAYS_LATE',
    range: [now.clone().subtract(2, 'day').format(DATE_FORMAT), now.clone().subtract(2, 'day').format(DATE_FORMAT)]
  },
  {
    text: '3_DAYS_LATE',
    range: [now.clone().subtract(3, 'day').format(DATE_FORMAT), now.clone().subtract(3, 'day').format(DATE_FORMAT)]
  },
  {
    text: '2_TO_3_DAYS_LATE',
    range: [now.clone().subtract(3, 'day').format(DATE_FORMAT), now.clone().subtract(2, 'day').format(DATE_FORMAT)]
  },
  {
    text: '4_TO_5_DAYS_LATE',
    range: [now.clone().subtract(5, 'day').format(DATE_FORMAT), now.clone().subtract(4, 'day').format(DATE_FORMAT)]
  },
  {
    text: 'MORE_THAN_3_DAYS_LATE',
    range: [moment(MIN_DATE).format(DATE_FORMAT), now.clone().subtract(4, 'day').format(DATE_FORMAT)]
  },
  {
    text: 'MORE_THAN_5_DAYS_LATE',
    range: [moment(MIN_DATE).format(DATE_FORMAT), now.clone().subtract(6, 'day').format(DATE_FORMAT)]
  },
  {
    text: '1_DAY_PRIOR',
    range: [now.clone().add(1, 'day').format(DATE_FORMAT), now.clone().add(1, 'day').format(DATE_FORMAT)]
  },
  {
    text: '2_DAY_PRIOR',
    range: [now.clone().add(2, 'day').format(DATE_FORMAT), now.clone().add(2, 'day').format(DATE_FORMAT)]
  },
  {
    text: '3_DAY_PRIOR',
    range: [now.clone().add(3, 'day').format(DATE_FORMAT), now.clone().add(3, 'day').format(DATE_FORMAT)]
  },
  {
    text: 'ALL',
    range: [moment(MIN_DATE).format(DATE_FORMAT), now.format(DATE_FORMAT)]
  }
];

export const TABS = {
  LIST: 'LIST',
  REPORTS: 'REPORTS'
};

const tabItems = [
  {
    value: TABS.LIST,
    text: TABS.LIST,
    icon: faPhoneAlt
  },
  {
    value: TABS.REPORTS,
    text: TABS.REPORTS,
    icon: faChartBar
  }
];

export default {
  name: 'PaymentReminder',
  components: {
    TextInput,
    FontAwesomeIcon,
    FilterBox,
    DateFilter,
    ClientDetailsModal,
    ToggleButton,
    IconButton,
    PastDueClients,
    TabMenu,
    Reports
  },
  metaInfo() {
    const { $i18n } = this;
    const title = $i18n.t('PAYMENT_REMINDER');

    return {
      title,
      meta: []
    };
  },
  setup(props, { root: { $router, $route }, refs }) {
    const searchInput = ref('');
    const currentTab = ref($route.meta.module || TABS.LIST);

    const { total, loanBalance, filters } = useState(['total', 'loanBalance', 'filters']);
    const { isDesktop } = useAppState(['isDesktop']);
    const { applyFilter } = useActions(['applyFilter', 'targetClient']);

    const filter = (value) => {
      applyFilter({
        ...value
      });
    };

    const search = () => {
      applyFilter({
        term: searchInput.value,
        page: 1,
      });
    };

    const setDate = (from, to) => {
      applyFilter({
        fromDate: from,
        toDate: to,
        page: 1
      });
    };

    const toggleHasValueAdjustment = () => {
      applyFilter({
        hasValueAdjustment: !filters.value.hasValueAdjustment,
        page: 1
      });
    };

    const toggleDateFilter = () => {
      refs.dateFilter.toggle(true);
    };

    watch(currentTab, (tab) => {
      if (tab === TABS.LIST) {
        $router.replace(PATHS.PAYMENT_REMINDER);
      } else if (tab === TABS.REPORTS) {
        $router.replace(PATHS.PAYMENT_REMINDER_REPORTS);
      }
    });

    onMounted(() => {
      searchInput.value = filters.value.term;
    });

    return {
      total,
      loanBalance,
      isDesktop,
      filters,
      faSearch,
      faFunnelDollar,
      faCalendarAlt,
      searchInput,
      addCommas,
      filter,
      search,
      setDate,
      toggleHasValueAdjustment,
      toggleDateFilter,
      currentTab,
      TABS,
      tabItems,
      datePresets
    };
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/scss/colors";

#payment-reminder {
  padding: 30px;

  h1 {
    font-size: 28px;
    text-transform: capitalize;
  }

  ul {
    padding-left: 0px;
  }

  li {
    display: inline;

      span {
        text-transform: capitalize;
      }

      span.highlight {
        color: $geranium;
        padding-right: 20px;
      }
  }

  .filters {
    display: flex;
    justify-content: flex-end;

    @media (min-width: 1311px) {
      margin-bottom: -37px;
    }
    margin-bottom: 10px;

    .input {
      width: 300px;
    }

    #filter-box, .toggle-box, >.toggle-button {
      margin-left: 10px;
    }
  }

  .icon-buttons {
    margin: 15px 0px;
  }

  @media (min-width: 992px) {
    .tab-menu {
      width: 400px;
      display: inline-block;
    }
  }
}

</style>
